import type ExcelJSNamespace from "exceljs";
import type { Workbook, Worksheet, CellValue } from "exceljs";
import { Item } from "./InventoryContext";
import { QTY_SCALE, isWholeNumber, roundMoney, roundQty } from "../../util/quantity";

/**
 * Inventory count <-> Excel round trip.
 *
 * Sheet protection only guards against slips (it is trivial to remove), so
 * the import trusts nothing but the hidden item id, Count and Unit value and
 * re-validates all three.
 */

export const SHEET_FORMAT_VERSION = 1;
export const COUNT_SHEET = "Count";
export const INFO_SHEET = "Info";

type ExcelJS = typeof ExcelJSNamespace;

/** Row shape returned by GET depots/:id/items. */
export type DepotItemRow = {
	id: number;
	ItemCode?: string | null;
	ItemName?: string | null;
	CostPrice?: number | string | null;
	Stock?: number | string | null;
	Sector?: string | null;
	Category?: string | null;
	IsFractional?: boolean;
	InDepot?: boolean;
};

export type InventorySheetMeta = {
	taskId: string;
	depotId: number;
	depotName: string;
	date: string;
	reference: string;
	invType: string | null;
	exportedAt: string;
	exportedBy: string;
};

export type InventorySheetLabels = {
	id: string;
	rowNumber: string;
	sector: string;
	category: string;
	itemCode: string;
	item: string;
	stock: string;
	count: string;
	fractional: string;
	unitValue: string;
	stockDifference: string;
	stockValue: string;
	countValue: string;
	valueDifference: string;
	inCount: string;
	notCounted: string;
	total: string;
	info: Record<keyof InventorySheetMeta | "formatVersion" | "rows" | "instructions", string>;
	instructionsText: string;
};

// Column layout (1-based). Only COUNT and UNIT_VALUE are unlocked.
export const COL = {
	ID: 1,
	ROW: 2,
	SECTOR: 3,
	CATEGORY: 4,
	CODE: 5,
	ITEM: 6,
	STOCK: 7,
	COUNT: 8,
	FRACTIONAL: 9,
	UNIT_VALUE: 10,
	STOCK_DIFF: 11,
	STOCK_VALUE: 12,
	COUNT_VALUE: 13,
	VALUE_DIFF: 14,
} as const;

const LAST_COL = COL.VALUE_DIFF;
const letter = (col: number) => String.fromCharCode(64 + col);

const num = (value: unknown): number => {
	const n = Number(value);
	return Number.isFinite(n) ? n : 0;
};

type ExportLine = {
	id: number;
	sector: string;
	category: string;
	code: string;
	name: string;
	stock: number;
	count: number | null;
	unitValue: number;
	fractional: boolean;
	inTask: boolean;
};

export const buildExportLines = (taskLines: readonly Item[], items: readonly DepotItemRow[]): ExportLine[] => {
	const inTask = new Set(taskLines.map((line) => Number(line.id)));
	const byId = new Map(items.map((item) => [Number(item.id), item]));

	const counted: ExportLine[] = taskLines.map((line) => {
		const source = byId.get(Number(line.id));
		return {
			id: Number(line.id),
			sector: line.sector || source?.Sector || "",
			category: line.category || source?.Category || "",
			code: line.item_code || source?.ItemCode || "",
			name: line.item || source?.ItemName || "",
			// Current system stock, so the file's snapshot is fresh at export.
			stock: source ? num(source.Stock) : num(line.stock),
			count: num(line.quantity),
			unitValue: num(line.price),
			fractional: line.isFractional,
			inTask: true,
		};
	});

	const rest = items
		.filter((item) => !inTask.has(Number(item.id)))
		// Items the system holds in this depot first, then other catalog items.
		.sort((a, b) => Number(a.InDepot === false) - Number(b.InDepot === false))
		.map<ExportLine>((item) => ({
			id: Number(item.id),
			sector: item.Sector || "",
			category: item.Category || "",
			code: item.ItemCode || "",
			name: item.ItemName || "",
			stock: num(item.Stock),
			count: null,
			unitValue: num(item.CostPrice),
			fractional: item.IsFractional === true,
			inTask: false,
		}));

	return [...counted, ...rest];
};

const HEADER_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE9ECEF" } } as const;
const GROUP_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8F9FA" } } as const;
const EDIT_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF8DC" } } as const;

// Exact validation bounds for DECIMAL(18,4) quantities and DECIMAL(18,2) money.
// Written as strings: the float limits (e.g. 1e16 - 0.01) are not representable,
// and a whole-number rule with a fractional bound makes Excel repair the file.
export const QTY_WHOLE_MAX = "99999999999999";
export const QTY_DECIMAL_MAX = `99999999999999.${"9".repeat(QTY_SCALE)}`;
export const MONEY_DECIMAL_MAX = "9999999999999999.99";
// Exclusive upper bounds for import (DECIMAL(18,4) / DECIMAL(18,2) integer parts).
const QTY_CEILING = 1e14;
const MONEY_CEILING = 1e16;

const QTY_WHOLE_FMT = "#,##0";
const QTY_FRACTION_FMT = `#,##0.${"#".repeat(QTY_SCALE)}`;
const MONEY_FMT = "#,##0.00";

const protectSheet = async (sheet: Worksheet) => {
	await sheet.protect("", {
		selectLockedCells: true,
		selectUnlockedCells: true,
		formatColumns: true,
		sort: true,
		autoFilter: true,
	});
};

export const buildInventoryWorkbook = async (
	ExcelJS: ExcelJS,
	{
		meta,
		taskLines,
		items,
		labels,
	}: {
		meta: InventorySheetMeta;
		taskLines: readonly Item[];
		items: readonly DepotItemRow[];
		labels: InventorySheetLabels;
	},
): Promise<Workbook> => {
	const workbook = new ExcelJS.Workbook();
	workbook.creator = meta.exportedBy || "MOLe";
	workbook.created = new Date(meta.exportedAt);

	const lines = buildExportLines(taskLines, items);
	const sheet = workbook.addWorksheet(COUNT_SHEET, {
		views: [{ state: "frozen", ySplit: 1 }],
	});

	sheet.columns = [
		{ key: "id", width: 8, hidden: true },
		{ key: "row", width: 6 },
		{ key: "sector", width: 16 },
		{ key: "category", width: 16 },
		{ key: "code", width: 14 },
		{ key: "item", width: 40 },
		{ key: "stock", width: 12 },
		{ key: "count", width: 12 },
		{ key: "fractional", width: 6, hidden: true },
		{ key: "unitValue", width: 14 },
		{ key: "stockDiff", width: 12 },
		{ key: "stockValue", width: 16 },
		{ key: "countValue", width: 16 },
		{ key: "valueDiff", width: 16 },
	];

	const header = sheet.getRow(1);
	header.values = [
		labels.id,
		labels.rowNumber,
		labels.sector,
		labels.category,
		labels.itemCode,
		labels.item,
		labels.stock,
		labels.count,
		labels.fractional,
		labels.unitValue,
		labels.stockDifference,
		labels.stockValue,
		labels.countValue,
		labels.valueDifference,
	];
	header.font = { bold: true };
	header.eachCell((cell) => {
		cell.fill = HEADER_FILL;
	});

	let rowIndex = 1;
	const firstDataRow = 2;
	const addGroupRow = (label: string) => {
		rowIndex += 1;
		const row = sheet.getRow(rowIndex);
		row.getCell(COL.ITEM).value = label;
		row.font = { bold: true, italic: true };
		for (let col = 1; col <= LAST_COL; col += 1) row.getCell(col).fill = GROUP_FILL;
	};

	let lineNumber = 0;
	let previousInTask: boolean | null = null;
	for (const line of lines) {
		if (line.inTask !== previousInTask) {
			addGroupRow(line.inTask ? labels.inCount : labels.notCounted);
			previousInTask = line.inTask;
		}
		rowIndex += 1;
		lineNumber += 1;
		const r = rowIndex;
		const row = sheet.getRow(r);
		row.getCell(COL.ID).value = line.id;
		row.getCell(COL.ROW).value = lineNumber;
		row.getCell(COL.SECTOR).value = line.sector;
		row.getCell(COL.CATEGORY).value = line.category;
		// Text, so leading zeros in codes survive.
		row.getCell(COL.CODE).value = String(line.code);
		row.getCell(COL.CODE).numFmt = "@";
		row.getCell(COL.ITEM).value = line.name;
		row.getCell(COL.STOCK).value = line.stock;
		row.getCell(COL.FRACTIONAL).value = line.fractional ? 1 : 0;

		const qtyFmt = line.fractional ? QTY_FRACTION_FMT : QTY_WHOLE_FMT;
		row.getCell(COL.STOCK).numFmt = qtyFmt;

		const count = row.getCell(COL.COUNT);
		count.value = line.count;
		count.numFmt = qtyFmt;
		count.protection = { locked: false };
		count.fill = EDIT_FILL;
		count.dataValidation = {
			type: line.fractional ? "decimal" : "whole",
			operator: "between",
			allowBlank: true,
			showErrorMessage: true,
			formulae: [0, line.fractional ? QTY_DECIMAL_MAX : QTY_WHOLE_MAX],
		};

		const unitValue = row.getCell(COL.UNIT_VALUE);
		unitValue.value = line.unitValue;
		unitValue.numFmt = MONEY_FMT;
		unitValue.protection = { locked: false };
		unitValue.fill = EDIT_FILL;
		unitValue.dataValidation = {
			type: "decimal",
			operator: "between",
			allowBlank: true,
			showErrorMessage: true,
			formulae: [0, MONEY_DECIMAL_MAX],
		};

		const H = `${letter(COL.COUNT)}${r}`;
		const G = `${letter(COL.STOCK)}${r}`;
		const J = `${letter(COL.UNIT_VALUE)}${r}`;
		const L = `${letter(COL.STOCK_VALUE)}${r}`;
		const M = `${letter(COL.COUNT_VALUE)}${r}`;
		row.getCell(COL.STOCK_DIFF).value = { formula: `IF(${H}="","",${H}-${G})` };
		row.getCell(COL.STOCK_DIFF).numFmt = qtyFmt;
		row.getCell(COL.STOCK_VALUE).value = { formula: `${G}*${J}` };
		row.getCell(COL.COUNT_VALUE).value = { formula: `IF(${H}="","",${H}*${J})` };
		row.getCell(COL.VALUE_DIFF).value = { formula: `IF(${H}="","",${M}-${L})` };
		for (const col of [COL.STOCK_VALUE, COL.COUNT_VALUE, COL.VALUE_DIFF]) {
			row.getCell(col).numFmt = MONEY_FMT;
		}
	}

	const lastDataRow = rowIndex;
	rowIndex += 1;
	const totals = sheet.getRow(rowIndex);
	totals.getCell(COL.ITEM).value = labels.total;
	for (const col of [COL.STOCK_VALUE, COL.COUNT_VALUE, COL.VALUE_DIFF]) {
		const c = letter(col);
		totals.getCell(col).value = { formula: `SUM(${c}${firstDataRow}:${c}${Math.max(lastDataRow, firstDataRow)})` };
		totals.getCell(col).numFmt = MONEY_FMT;
	}
	totals.font = { bold: true };

	sheet.autoFilter = { from: { row: 1, column: COL.ROW }, to: { row: 1, column: LAST_COL } };
	await protectSheet(sheet);

	const info = workbook.addWorksheet(INFO_SHEET);
	info.columns = [
		{ key: "key", width: 16, hidden: true },
		{ key: "label", width: 22 },
		{ key: "value", width: 48 },
	];
	const infoRows: [string, string, string | number][] = [
		["formatVersion", labels.info.formatVersion, SHEET_FORMAT_VERSION],
		["taskId", labels.info.taskId, meta.taskId],
		["depotId", labels.info.depotId, meta.depotId],
		["depotName", labels.info.depotName, meta.depotName],
		["date", labels.info.date, meta.date],
		["reference", labels.info.reference, meta.reference],
		["invType", labels.info.invType, meta.invType ?? ""],
		["exportedAt", labels.info.exportedAt, meta.exportedAt],
		["exportedBy", labels.info.exportedBy, meta.exportedBy],
		["rows", labels.info.rows, lines.length],
		["instructions", labels.info.instructions, labels.instructionsText],
	];
	infoRows.forEach((values) => {
		const row = info.addRow(values);
		row.getCell(2).font = { bold: true };
		row.getCell(3).alignment = { wrapText: true, vertical: "top" };
	});
	await protectSheet(info);

	return workbook;
};

export const inventorySheetFileName = (meta: InventorySheetMeta) => {
	const safe = (value: string) => String(value || "").replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "");
	return `inventory_${safe(meta.depotName) || meta.depotId}_${safe(meta.reference)}_${meta.date}.xlsx`;
};

// ------------------------------------------------------------------ import

export type ParsedSheetRow = {
	rowNumber: number;
	id: number;
	name: string;
	fileStock: number | null;
	count: CellReading;
	unitValue: CellReading;
};

type CellReading = { kind: "blank" } | { kind: "number"; value: number } | { kind: "invalid"; raw: string };

export type ParsedInventorySheet = {
	formatVersion: number | null;
	taskId: string;
	depotId: number | null;
	depotName: string;
	rows: ParsedSheetRow[];
};

export class InventorySheetError extends Error {
	constructor(public code: "missingSheet" | "badVersion") {
		super(code);
	}
}

const unwrap = (value: CellValue): unknown => {
	if (value === null || value === undefined) return null;
	if (typeof value === "object" && !(value instanceof Date)) {
		if ("result" in value) return (value as { result?: unknown }).result ?? null;
		if ("richText" in value) return (value as { richText: { text: string }[] }).richText.map((p) => p.text).join("");
		if ("text" in value) return (value as { text: string }).text;
	}
	return value;
};

export const readNumber = (value: CellValue): CellReading => {
	const raw = unwrap(value);
	if (raw === null || raw === "") return { kind: "blank" };
	if (typeof raw === "number") return Number.isFinite(raw) ? { kind: "number", value: raw } : { kind: "invalid", raw: String(raw) };
	if (typeof raw === "string") {
		const text = raw.trim().replace(/\s+/g, "");
		if (!text) return { kind: "blank" };
		// Tolerate a decimal comma typed into a text-formatted cell.
		const normalized = /^-?\d+,\d+$/.test(text) ? text.replace(",", ".") : text;
		const n = Number(normalized);
		return Number.isFinite(n) ? { kind: "number", value: n } : { kind: "invalid", raw };
	}
	return { kind: "invalid", raw: String(raw) };
};

export const parseInventoryWorkbook = (workbook: Workbook): ParsedInventorySheet => {
	const info = workbook.getWorksheet(INFO_SHEET);
	const sheet = workbook.getWorksheet(COUNT_SHEET);
	if (!info || !sheet) throw new InventorySheetError("missingSheet");

	const infoValues: Record<string, unknown> = {};
	info.eachRow((row) => {
		const key = unwrap(row.getCell(1).value);
		if (typeof key === "string" && key) infoValues[key] = unwrap(row.getCell(3).value);
	});
	const formatVersion = Number(infoValues.formatVersion);
	if (formatVersion !== SHEET_FORMAT_VERSION) throw new InventorySheetError("badVersion");

	const rows: ParsedSheetRow[] = [];
	sheet.eachRow((row, rowNumber) => {
		if (rowNumber === 1) return;
		const idReading = readNumber(row.getCell(COL.ID).value);
		// Group and total rows carry no id.
		if (idReading.kind !== "number" || !Number.isInteger(idReading.value) || idReading.value < 1) return;
		const stock = readNumber(row.getCell(COL.STOCK).value);
		rows.push({
			rowNumber,
			id: idReading.value,
			name: String(unwrap(row.getCell(COL.ITEM).value) ?? ""),
			fileStock: stock.kind === "number" ? stock.value : null,
			count: readNumber(row.getCell(COL.COUNT).value),
			unitValue: readNumber(row.getCell(COL.UNIT_VALUE).value),
		});
	});

	const depotId = Number(infoValues.depotId);
	return {
		formatVersion,
		taskId: String(infoValues.taskId ?? ""),
		depotId: Number.isFinite(depotId) ? depotId : null,
		depotName: String(infoValues.depotName ?? ""),
		rows,
	};
};

export type InventoryImportSummary = {
	updated: string[];
	added: string[];
	blank: number;
	invalid: { row: number; name: string; reason: "count" | "fraction" | "unitValue" }[];
	unknown: { row: number; id: number; name: string }[];
	drifted: { name: string; fileStock: number; currentStock: number }[];
	duplicates: string[];
};

export type InventoryImportResult =
	| { status: "wrongDepot"; fileDepotName: string }
	| { status: "ok"; taskMismatch: boolean; lines: Item[]; summary: InventoryImportSummary };

const sameQty = (a: number, b: number) => Math.abs(a - b) < 1 / 10 ** QTY_SCALE / 2;

export const applyInventoryImport = ({
	parsed,
	taskId,
	depotId,
	taskLines,
	items,
}: {
	parsed: ParsedInventorySheet;
	taskId: string;
	depotId: number;
	taskLines: readonly Item[];
	items: readonly DepotItemRow[];
}): InventoryImportResult => {
	if (parsed.depotId !== Number(depotId)) {
		return { status: "wrongDepot", fileDepotName: parsed.depotName };
	}

	const summary: InventoryImportSummary = {
		updated: [],
		added: [],
		blank: 0,
		invalid: [],
		unknown: [],
		drifted: [],
		duplicates: [],
	};
	const itemsById = new Map(items.map((item) => [Number(item.id), item]));
	const lines = taskLines.map((line) => new Item({ ...line, isFractional: line.isFractional }));
	const lineIndex = new Map(lines.map((line, index) => [Number(line.id), index]));
	const seen = new Set<number>();
	const updatedIds = new Set<number>();
	const driftedIds = new Set<number>();

	for (const row of parsed.rows) {
		const source = itemsById.get(row.id);
		const existingIndex = lineIndex.get(row.id);
		const existing = existingIndex !== undefined ? lines[existingIndex] : undefined;
		const name = existing?.item || source?.ItemName || row.name;

		if (!source && !existing) {
			summary.unknown.push({ row: row.rowNumber, id: row.id, name: row.name });
			continue;
		}
		if (seen.has(row.id)) summary.duplicates.push(name);
		seen.add(row.id);

		if (row.count.kind === "blank") {
			summary.blank += 1;
			continue;
		}
		if (row.count.kind === "invalid" || row.count.value < 0 || row.count.value >= QTY_CEILING) {
			summary.invalid.push({ row: row.rowNumber, name, reason: "count" });
			continue;
		}
		const fractional = existing ? existing.isFractional : source?.IsFractional === true;
		if (!fractional && !isWholeNumber(row.count.value)) {
			summary.invalid.push({ row: row.rowNumber, name, reason: "fraction" });
			continue;
		}
		if (
			row.unitValue.kind === "invalid" ||
			(row.unitValue.kind === "number" && (row.unitValue.value < 0 || row.unitValue.value >= MONEY_CEILING))
		) {
			summary.invalid.push({ row: row.rowNumber, name, reason: "unitValue" });
			continue;
		}

		const quantity = fractional ? roundQty(row.count.value) : Math.round(row.count.value);
		const currentStock = source ? num(source.Stock) : num(existing?.stock);
		const price =
			row.unitValue.kind === "number"
				? roundMoney(row.unitValue.value)
				: existing
					? num(existing.price)
					: num(source?.CostPrice);

		if (row.fileStock !== null && !sameQty(row.fileStock, currentStock) && !driftedIds.has(row.id)) {
			driftedIds.add(row.id);
			summary.drifted.push({ name, fileStock: row.fileStock, currentStock });
		}

		const next = new Item({
			id: row.id,
			item_code: existing?.item_code || source?.ItemCode || "",
			item: name,
			price,
			stock: currentStock,
			quantity,
			sector: existing?.sector || source?.Sector || "",
			category: existing?.category || source?.Category || "",
			isFractional: fractional,
		});

		if (existingIndex !== undefined) {
			lines[existingIndex] = next;
			if (!updatedIds.has(row.id)) summary.updated.push(name);
		} else {
			lineIndex.set(row.id, lines.length);
			lines.push(next);
			summary.added.push(name);
		}
		updatedIds.add(row.id);
	}

	return {
		status: "ok",
		taskMismatch: !!parsed.taskId && parsed.taskId !== taskId,
		lines,
		summary,
	};
};
